import { Link } from "react-router-dom";
import { MapPin, ChevronRight, Users, Shield, BatteryCharging } from "lucide-react";
import chargeflipLogo from "@/assets/chargeflip-logo.png";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const Index = () => {
  const { data: hostCount } = useQuery({
    queryKey: ["host_count"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_host_count");
      if (error) return 0;
      return Number(data) || 0;
    },
  });

  const { data: driverCount } = useQuery({
    queryKey: ["driver_waitlist_count"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_driver_waitlist_count");
      if (error) return 0;
      return Number(data) || 0;
    },
  });

  return (
    <div className="min-h-screen bg-background">
      {/* Breadcrumb JSON-LD for Google */}
      {/* Nav */}
      <nav className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="container flex h-14 items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={chargeflipLogo} alt="chargeflip Logo" className="h-8 w-auto" />
          </Link>
          {/* Admin-Zugang über /login erreichbar */}
        </div>
      </nav>

      <main>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-primary/6 to-background" />
        <div className="container flex flex-col items-center py-20 text-center md:py-28 lg:py-36">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-1.5 text-sm font-medium text-primary">
            <BatteryCharging className="h-4 w-4" />
            Pre-Launch — Jetzt früh dabei sein
          </div>
          <h1
            className="max-w-3xl text-3xl font-bold leading-[1.1] tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-[3.5rem]"
            style={{ textWrap: "balance" }}
          >
            Werde Teil des ersten privaten E-Auto-Ladenetzwerks im ländlichen Raum
          </h1>
          <p
            className="mt-5 max-w-xl text-base text-muted-foreground sm:text-lg"
            style={{ textWrap: "pretty" }}
          >
            Verdiene Geld mit deiner Wallbox oder finde im Notfall immer Strom — einfach, privat und direkt in deiner Nachbarschaft.
          </p>
          <div className="mt-10 flex flex-col gap-3 sm:flex-row">
            <Link to="/host-signup">
              <Button size="lg" className="gap-2 px-8 text-base">
                <BatteryCharging className="h-5 w-5" />
                Ladepunkt anbieten
              </Button>
            </Link>
            <Link to="/driver-waitlist">
              <Button size="lg" variant="outline" className="gap-2 px-8 text-base">
                Ich brauche Strom!
                <ChevronRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Counter bar */}
      <section className="border-y border-border bg-muted/40">
        <div className="container grid grid-cols-1 gap-6 py-8 sm:grid-cols-2">
          <div className="flex flex-col items-center text-center">
            <span className="text-3xl font-bold tabular-nums text-primary">{hostCount ?? 0}</span>
            <span className="mt-1 text-sm text-muted-foreground">Registrierte Ladepunkte</span>
          </div>
          <div className="flex flex-col items-center text-center">
            <span className="text-3xl font-bold tabular-nums text-primary">{driverCount ?? 0}</span>
            <span className="mt-1 text-sm text-muted-foreground">Fahrer auf der Warteliste</span>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20">
        <div className="container">
          <h2 className="text-center text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
            So funktioniert's
          </h2>
          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {[
              {
                icon: MapPin,
                title: "Ladepunkt registrieren",
                desc: "Du hast eine Wallbox? Melde sie an und werde Teil des Netzwerks — noch bevor wir starten.",
              },
              {
                icon: Users,
                title: "Netzwerk wächst",
                desc: "Fahrer registrieren sich in deiner Region. Sobald genügend Teilnehmer da sind, geht es los.",
              },
              {
                icon: BatteryCharging,
                title: "Laden & Verdienen",
                desc: "Nach dem Launch können Fahrer buchen und laden. Du verdienst bei jeder Nutzung.",
              },
            ].map((s, i) => (
              <div
                key={s.title}
                className="relative rounded-xl border border-border bg-card p-7 transition-shadow hover:shadow-md"
              >
                <span className="absolute right-5 top-5 text-sm font-medium text-muted-foreground/50" aria-hidden="true">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <s.icon className="h-5 w-5" />
                </div>
                <h3 className="text-base font-semibold text-foreground">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="border-t border-border bg-primary/5 py-16">
        <div className="container flex flex-col items-center text-center">
          <Shield className="h-9 w-9 text-primary" />
          <h2 className="mt-5 text-xl font-bold text-foreground sm:text-2xl">
           Geprüft & sicher
          </h2>
          <p className="mt-3 max-w-md text-sm text-muted-foreground leading-relaxed">
             Jeder Ladepunkt wird von uns geprüft, bevor er freigeschaltet wird.
             Bezahlung und Daten laufen verschlüsselt – ohne Aufwand für dich.
          </p>
          <Link to="/host-signup" className="mt-8">
            <Button size="lg">Jetzt mitmachen</Button>
          </Link>
        </div>
      </section>

      </main>
      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="container flex flex-col items-center justify-between gap-4 sm:flex-row">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <img src={chargeflipLogo} alt="chargeflip" className="h-4 w-auto" />
            <span>© 2026 chargeflip</span>
          </div>
          <div className="flex gap-6 text-sm text-muted-foreground">
            <Link to="/impressum" className="transition-colors hover:text-foreground">Impressum</Link>
            <a href="#" className="transition-colors hover:text-foreground">Datenschutz</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
